import { useCallback } from "react";
import { ServerMessages, useSocketContext } from "../../context/SocketContext";
import { CreateStaffPayload, LocationStaff, StaffRoleLevels, UpdateStaffPayload } from "../../types/Staff";
import { eventBus } from "../../common/eventBus";

const useStaffService = () => {
  const { emit } = useSocketContext();

  const fetchAllStaff = useCallback(async (): Promise<LocationStaff[]> => {
    const response = await emit({
      message: ServerMessages.listStaff,
    });

    return response?.data ?? [];
  }, [emit]);

  const createStaffMember = useCallback(
    async (payload: CreateStaffPayload): Promise<number> => {
      const { role, locationId, ...staffPayload } = payload;
      const createResponse = await emit<{ staffId: number }>({
        message: ServerMessages.createStaff,
        options: staffPayload,
      });

      const staffId = createResponse?.data?.staffId;
      if (!staffId) {
        throw new Error("createStaff did not return a staffId");
      }

      await emit({
        message: ServerMessages.setStaffRoleLevel,
        options: {
          staffId,
          locationId: payload.locationId,
          roleLevel: StaffRoleLevels[role],
        },
      });

      eventBus.emit('staffCreated', { staffId, locationId: payload.locationId });

      return staffId;
    }, [emit],
  );

  const updateStaffMember = useCallback(
    async (payload: UpdateStaffPayload): Promise<void> => {
      // todo remove this after be update
      const { role, locationId, ...updateFields } = payload;

      const res = await emit({
        message: ServerMessages.updateStaff,
        options: updateFields,
      });

      if (role) {
        await emit({
          message: ServerMessages.setStaffRoleLevel,
          options: {
            staffId: payload.staffId,
            locationId: payload.locationId,
            roleLevel: StaffRoleLevels[role],
          },
        });
      }

      eventBus.emit('staffEdited');

      return res?.data;
    }, [emit],
  );

  const deleteStaffMember = useCallback(
    async (staffId: number): Promise<void> => {
      const response = await emit({
        message: ServerMessages.deleteStaff,
        options: { staffId },
      });

      eventBus.emit('staffDeleted', { staffId });

      return response?.data;
    }, [emit],
  );

  const changeStaffPassword = useCallback(
    async (staffId: number, newPassword: string): Promise<void> => {
      await emit({
        message: ServerMessages.updateStaff,
        options: {
          staffId,
          password: newPassword,
        },
      });
    }, [emit],
  );

  return {
    fetchAllStaff,
    createStaffMember,
    deleteStaffMember,
    updateStaffMember,
    changeStaffPassword,
  };
};

export default useStaffService;
